/**
 * DOMService
 * Handles DOM manipulation for i18n (HTML lang and dir attributes)
 * Implements Single Responsibility Principle by separating DOM concerns
 */

import type { IDOMService } from '../../types/i18n';
import { RTL_LANGUAGES } from '../../config/i18n.config';

export class DOMService implements IDOMService {
  /**
   * Update HTML lang and dir attributes for accessibility
   * @param language Language code to apply
   */
  updateHTMLAttributes(language: string): void {
    try {
      const html = document.documentElement;

      // Update lang attribute
      html.lang = language;

      // Update dir attribute
      const isRTL = RTL_LANGUAGES.includes(language);
      html.dir = isRTL ? 'rtl' : 'ltr';
    } catch (error) {
      console.warn('[DOMService] Error updating HTML attributes:', error);
    }
  }

  /**
   * Get current HTML lang attribute
   * @returns Current language from HTML element
   */
  getCurrentHTMLLanguage(): string | null {
    try {
      return document.documentElement.lang || null;
    } catch (error) {
      console.warn('[DOMService] Error getting HTML language:', error);
      return null;
    }
  }

  /**
   * Get current HTML dir attribute
   * @returns Current direction from HTML element
   */
  getCurrentHTMLDirection(): 'ltr' | 'rtl' | null {
    try {
      const dir = document.documentElement.dir;
      return dir === 'rtl' || dir === 'ltr' ? dir : null;
    } catch (error) {
      console.warn('[DOMService] Error getting HTML direction:', error);
      return null;
    }
  }
}

// Export singleton instance for convenience (but allow injection for testing)
export const domService = new DOMService();
